/**
 * The grouper: signal set in, bucket out. Pure.
 *
 * Buckets come from the admin-edited sales.newBusiness config and are tried in
 * `order` (ties broken by array position); the first whose rules hold wins.
 * No match falls through to the config's `unclassified` entry, which the
 * report flags so somebody fixes the opportunity rather than the number
 * silently landing in "Other".
 */

import type {
  NewBusinessBucketConfig,
  NewBusinessFamily,
  NewBusinessGroupCategory,
  NewBusinessValue,
} from '../../entities/admin-config/model/types';
import { normalizeSignal } from './signals';

export const UNCLASSIFIED_KEY = 'unclassified';

export interface ClassifiedDeal {
  bucketKey: string;
  label: string;
  family: NewBusinessFamily;
  program: string | null;
  weight: number;
  unclassified: boolean;
}

export function orderedBuckets(config: NewBusinessValue): NewBusinessBucketConfig[] {
  return config.buckets
    .map((b, i) => ({ b, i }))
    .sort((x, y) => (x.b.order - y.b.order) || (x.i - y.i))
    .map(({ b }) => b);
}

function toKeySet(values: readonly string[]): Set<string> {
  return new Set(values.map(normalizeSignal).filter(Boolean));
}

export function bucketMatches(bucket: NewBusinessBucketConfig, signals: readonly string[]): boolean {
  const have = toKeySet(signals);
  const all = toKeySet(bucket.requireAll);
  const any = toKeySet(bucket.requireAny);
  const not = toKeySet(bucket.exclude);
  // A bucket with no positive rule at all never matches; it would swallow
  // every deal the moment an admin left the rule cells blank.
  if (all.size === 0 && any.size === 0) return false;
  for (const s of all) if (!have.has(s)) return false;
  if (any.size > 0) {
    let hit = false;
    for (const s of any) {
      if (have.has(s)) {
        hit = true;
        break;
      }
    }
    if (!hit) return false;
  }
  for (const s of not) if (have.has(s)) return false;
  return true;
}

export function classifySignals(signals: readonly string[], config: NewBusinessValue): ClassifiedDeal {
  for (const b of orderedBuckets(config)) {
    if (bucketMatches(b, signals)) {
      return {
        bucketKey: b.key,
        label: b.label,
        family: b.family,
        program: b.program ?? null,
        weight: b.weight,
        unclassified: false,
      };
    }
  }
  return {
    bucketKey: UNCLASSIFIED_KEY,
    label: config.unclassified.label,
    family: 'other',
    program: null,
    weight: config.unclassified.weight,
    unclassified: true,
  };
}

/** Display attributes for a bucket key, from the CURRENT config. */
export function resolveBucket(
  key: string,
  config: NewBusinessValue,
): { label: string; family: NewBusinessFamily; program: string | null; weight: number } | undefined {
  if (key === UNCLASSIFIED_KEY) {
    return {
      label: config.unclassified.label,
      family: 'other',
      program: null,
      weight: config.unclassified.weight,
    };
  }
  const b = config.buckets.find((x) => x.key === key);
  return b ? { label: b.label, family: b.family, program: b.program ?? null, weight: b.weight } : undefined;
}

// ---------------------------------------------------------------------------
// Weighted group count (Sasha 10/05). A bucket points at a group category; the
// category's weight is how many groups one deal counts as. Render time, never
// stamped.
// ---------------------------------------------------------------------------

export const OTHER_GROUP_CATEGORY_KEY = 'other';

export function orderedGroupCategories(config: NewBusinessValue): NewBusinessGroupCategory[] {
  return (config.groupCategories ?? [])
    .map((c, i) => ({ c, i }))
    .sort((x, y) => (x.c.order - y.c.order) || (x.i - y.i))
    .map(({ c }) => c);
}

/** The category key a bucket's deals count under, derived when the bucket names none. */
export function groupCategoryKeyOf(bucketKey: string, config: NewBusinessValue): string {
  if (bucketKey === UNCLASSIFIED_KEY) return OTHER_GROUP_CATEGORY_KEY;
  const b = config.buckets.find((x) => x.key === bucketKey);
  if (!b) return OTHER_GROUP_CATEGORY_KEY;
  if (b.groupCategory) return b.groupCategory;
  if ((b.program ?? '').trim().toLowerCase() === 'mec') return 'mec';
  if (b.family === 'mec') return 'mec';
  if (b.family === 'tpa') return 'tpa';
  if (b.family === 'rx') return 'rx-only';
  return OTHER_GROUP_CATEGORY_KEY;
}

/**
 * The category a bucket's deals count under, from the CURRENT config. A key
 * the table no longer carries counts as one group under its own key, so a
 * deleted row shows up instead of vanishing from the count.
 */
export function resolveGroupCategory(bucketKey: string, config: NewBusinessValue): NewBusinessGroupCategory {
  const key = groupCategoryKeyOf(bucketKey, config);
  const found = (config.groupCategories ?? []).find((c) => c.key === key);
  return found ?? { key, label: key === OTHER_GROUP_CATEGORY_KEY ? 'Other' : key, weight: 1, order: Number.MAX_SAFE_INTEGER };
}

/** Credited lives = actual lives x weight, to 2 decimals (Rose's 50.25). */
export function creditedLives(actualLives: number | null | undefined, weight: number): number {
  if (actualLives == null || !Number.isFinite(actualLives)) return 0;
  return Math.round(actualLives * weight * 100) / 100;
}

/**
 * Michael's revenue-based credit: annual revenue / 12 / revenuePerLifePerMonth.
 * Vigilant: $3M / 12 / 24 = 10,416.67. A suggestion for an override, never
 * applied on its own.
 */
export function creditFromRevenue(annualRevenue: number, revenuePerLifePerMonth: number): number {
  if (
    !Number.isFinite(annualRevenue) ||
    !Number.isFinite(revenuePerLifePerMonth) ||
    revenuePerLifePerMonth <= 0
  ) {
    return 0;
  }
  return Math.round((annualRevenue / 12 / revenuePerLifePerMonth) * 100) / 100;
}
